import axios from 'axios';
import { User } from '@/types/user'
import { ShiftType } from '@/types/shiftType';
import { apiCache } from './apiCache';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const userService = {
  async getAllUsers(includeInactive: boolean = false): Promise<User[]> {
    const cacheKey = includeInactive ? '/users?includeInactive=true' : '/users';
    return apiCache.getCachedData(
      cacheKey,
      async () => {
        const response = await apiClient.get('/users', {
          params: includeInactive ? { includeInactive: true } : {}
        });
        return response.data.users;
      },
      undefined,
      { cooldown: 10000 } // 10 second cooldown for user data
    );
  },

  async getEmployees(): Promise<User[]> {
    return apiCache.getCachedData(
      '/users?role=employee,manager',
      async () => {
        const response = await apiClient.get('/users?role=employee,manager');
        return response.data.users;
      },
      undefined,
      { cooldown: 10000 } // 10 second cooldown for employee data
    );
  },

  async getUserById(id: number): Promise<User> {
    const response = await apiClient.get(`/users/${id}`);
    return response.data.user;
  },

  async createUser(userData: any): Promise<User> {
    const response = await apiClient.post('/users', userData);
    apiCache.clearCache('/users');
    apiCache.clearCache('/users?role=employee,manager');
    return response.data.user;
  },

  async updateUser(id: number, userData: any): Promise<User> {
    const response = await apiClient.put(`/users/${id}`, userData);
    apiCache.clearCache('/users');
    apiCache.clearCache('/users?role=employee,manager');
    return response.data.user;
  },

  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
    apiCache.clearCache('/users');
    apiCache.clearCache('/users?role=employee,manager');
  },

  async activateUser(id: number): Promise<User> {
    const response = await apiClient.patch(`/users/${id}/activate`);
    return response.data.user;
  },

  async assignShiftTypesToUser(userId: number, shiftTypeIds: number[]): Promise<void> {
    await apiClient.post(`/users/${userId}/shift-types`, { shiftTypeIds });
  },

  async getUserShiftTypes(userId: number): Promise<ShiftType[]> {
    const response = await apiClient.get(`/users/${userId}/shift-types`);
    return response.data.shiftTypes;
  },

  async getUsersShiftTypes(userIds: number[]): Promise<Record<number, ShiftType[]>> {
    const response = await apiClient.post('/users/shift-types/batch', { userIds });
    return response.data.userShiftTypes;
  },

  async removeShiftTypeFromUser(userId: number, shiftTypeId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}/shift-types`, { data: { shiftTypeId } });
  },

  async sendPasswordResetLink(userId: number): Promise<{ message: string }> {
    const response = await apiClient.post(`/users/${userId}/send-reset-link`);
    return response.data;
  },
}; 